import { format } from 'date-fns';
import type { TFunction } from 'i18next';

import { recipientsLabel, splitRecipients, toRecipientValues } from '../RecipientsField.tsx';

type Schedule = any;

export const INTERVAL_HOURS = [1, 2, 3, 4, 6, 8, 12];
export const FREQUENCY_KEYS = ['daily', 'weekly', 'monthly', 'custom'] as const;
export type FrequencyKey = (typeof FREQUENCY_KEYS)[number];
export const EXPORT_FORMATS = ['message', 'xlsx', 'pdf', 'docx'] as const;
export const TIMEZONES = ['Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich', 'Europe/London', 'UTC'];

const time = (value?: string | null) => (value ? value.slice(0, 5) : '');

// backend frequencies → the dialog's four options; hourly / every_x_minutes are the custom interval
export const toFrequencyKey = (frequency: string): FrequencyKey =>
    frequency === 'daily' || frequency === 'weekly' || frequency === 'monthly' ? frequency : 'custom';

export const intervalHours = (schedule: { frequency: string; intervalMinutes?: number | null }) => {
    if (schedule.frequency === 'hourly') return 1;
    const hours = Math.round((schedule.intervalMinutes ?? 60) / 60);
    return INTERVAL_HOURS.includes(hours) ? hours : Math.max(1, Math.min(12, hours));
};

const cadenceParts = (t: TFunction, schedule: Schedule, prefix: string) => {
    const weekdays = t('schedule.weekdays', { returnObjects: true }) as string[];
    switch (toFrequencyKey(schedule.frequency)) {
        case 'daily':
            return t(`${prefix}.daily`, { time: time(schedule.timeOfDay) });
        case 'weekly':
            return t(`${prefix}.weekly`, { weekday: weekdays[schedule.weekday] ?? '', time: time(schedule.timeOfDay) });
        case 'monthly':
            return t(`${prefix}.monthly`, { day: schedule.dayOfMonth, time: time(schedule.timeOfDay) });
        default:
            return t(`${prefix}.everyHours`, { count: intervalHours(schedule) });
    }
};

export const formatCadence = (t: TFunction, schedule: Schedule) => cadenceParts(t, schedule, 'automations.cadence');

// the snake_case arguments of a chat CreateSchedule tool call, as the shape the cadence formatters read
export const fromToolArgs = (args: any): Schedule => ({
    frequency: args?.frequency,
    timeOfDay: args?.time_of_day,
    weekday: args?.weekday,
    dayOfMonth: args?.day_of_month,
    intervalMinutes: args?.interval_minutes,
    intervalUnit: args?.interval_unit,
    timezone: args?.timezone,
});

// the same arguments as dialog form state, for "Adjust details" on the chat proposal card
export const formFromToolArgs = (args: any): Partial<ScheduleFormState> => {
    const partial: Partial<ScheduleFormState> = {
        name: args?.name ?? '',
        prompt: args?.prompt ?? '',
        frequency: toFrequencyKey(args?.frequency ?? 'daily'),
    };
    if (args?.weekday != null) partial.weekday = args.weekday;
    if (args?.day_of_month != null) partial.dayOfMonth = args.day_of_month;
    if (args?.time_of_day) partial.timeOfDay = String(args.time_of_day).slice(0, 5);
    if (args?.interval_minutes) partial.intervalHours = Math.max(1, Math.round(args.interval_minutes / 60));
    if (args?.timezone) partial.timezone = args.timezone;
    if (args?.ends_on) {
        partial.endsMode = 'date';
        partial.endsOn = args.ends_on;
    }
    return partial;
};

export const formatCadenceCompact = (t: TFunction, schedule: Schedule) =>
    cadenceParts(t, schedule, 'automations.cadence.compact');

export const formatCadenceLong = (t: TFunction, schedule: Schedule) =>
    cadenceParts(t, schedule, 'automations.cadence.long');

// "Team Sales, Anna Schmidt" — the colleagues a run is also sent to (ENG-11387), empty when nobody
export const formatRecipients = (t: TFunction, schedule: Schedule) =>
    recipientsLabel(schedule.recipientTeam, schedule.recipients, t);

export const runErrorMessage = (t: TFunction, code?: string | null) =>
    code ? t(`automations.runErrors.${code}`, { defaultValue: t('automations.runErrors.RUN_FAILED') }) : '';

export const validationErrorMessage = (t: TFunction, error: unknown, fallbackKey: string): string => {
    const extensions = (error as any)?.graphQLErrors?.[0]?.extensions;
    return extensions?.code
        ? String(t(`automations.errors.${extensions.code}`, { ...extensions, defaultValue: t(fallbackKey) }))
        : String(t(fallbackKey));
};

export const formatRunDate = (iso: string) => format(new Date(iso), 'dd MMM');

export const formatRunTimestamp = (iso: string) => format(new Date(iso), 'EEE dd MMM yyyy, HH:mm');

export const formatRecentTimestamp = (iso: string) => format(new Date(iso), 'EEE dd MMM, HH:mm');

export const zoneName = (timezone: string) => timezone.split('/').pop()?.replace(/_/g, ' ') ?? timezone;

export const formatCadenceWithZone = (t: TFunction, schedule: Schedule) =>
    `${formatCadenceLong(t, schedule)} · ${t('automations.detail.timezoneLabel', { zone: zoneName(schedule.timezone || 'UTC') })}`;

export const formatNextRun = (iso: string, language: string, timeZone?: string | null) => {
    const options: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    };
    try {
        return new Intl.DateTimeFormat(language, { ...options, timeZone: timeZone || undefined }).format(new Date(iso));
    } catch {
        // an unknown zone falls back to the browser's
        return new Intl.DateTimeFormat(language, options).format(new Date(iso));
    }
};

export const RUNS_PAGE_SIZE = 10;

export const fromAutomationState = (schedule: { id: string; name: string }) => ({
    fromAutomation: { scheduleId: schedule.id, name: schedule.name },
});

export const formatDay = (date: string, language: string) =>
    new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short', year: 'numeric' }).format(
        new Date(`${date}T00:00:00`)
    );

export type ScheduleFormState = {
    name: string;
    prompt: string;
    frequency: FrequencyKey;
    weekday: number;
    dayOfMonth: number;
    useLastDayIfMissing: boolean;
    timeOfDay: string;
    intervalHours: number;
    endsMode: 'never' | 'date';
    endsOn: string | null; // 'yyyy-MM-dd'
    timezone: string;
    // colleagues who get each run in a chat of their own, as picker values (ENG-11387)
    recipients: string[];
    // the result by email to the user's own mailbox, nobody else's; exportFormat = the file it carries
    emailMe: boolean;
    exportFormat: string;
    notifyOnRun: boolean;
    selectedTools: string[];
    testRun: boolean;
};

export const toFormState = (
    schedule: Schedule,
    defaults: { prompt?: string; name?: string; timezone?: string; tools?: string[] }
): ScheduleFormState => ({
    name: schedule?.name ?? defaults.name ?? '',
    prompt: schedule?.prompt ?? defaults.prompt ?? '',
    frequency: schedule ? toFrequencyKey(schedule.frequency) : 'daily',
    weekday: schedule?.weekday ?? 0,
    dayOfMonth: schedule?.dayOfMonth ?? 1,
    useLastDayIfMissing: schedule?.useLastDayIfMissing ?? true,
    timeOfDay: time(schedule?.timeOfDay) || '08:00',
    intervalHours: schedule ? intervalHours(schedule) : 1,
    endsMode: schedule?.endsOn ? 'date' : 'never',
    endsOn: schedule?.endsOn ?? null,
    timezone: schedule?.timezone || defaults.timezone || 'Europe/Berlin',
    recipients: toRecipientValues(schedule?.recipientTeam, schedule?.recipients),
    emailMe: Boolean(schedule?.emailRecipients?.length),
    exportFormat: schedule?.exportFormat ?? 'message',
    notifyOnRun: schedule?.notifyOnRun ?? false,
    selectedTools: schedule?.selectedTools ?? defaults.tools ?? [],
    // on when creating and for a schedule paused after failures; off when editing
    testRun: !schedule || Boolean(schedule.autoPaused),
});

// ``mailbox`` is the user's own address when it can send; email goes there and nowhere else
export const toVariables = (form: ScheduleFormState, mailbox: string | null = null) => ({
    name: form.name.trim(),
    prompt: form.prompt.trim(),
    frequency: form.frequency === 'custom' ? 'every_x_minutes' : form.frequency,
    timeOfDay: form.frequency === 'custom' ? null : form.timeOfDay,
    weekday: form.frequency === 'weekly' ? form.weekday : null,
    dayOfMonth: form.frequency === 'monthly' ? form.dayOfMonth : null,
    useLastDayIfMissing: form.useLastDayIfMissing,
    intervalMinutes: form.frequency === 'custom' ? form.intervalHours * 60 : null,
    intervalUnit: form.frequency === 'custom' ? 'hours' : 'minutes',
    timezone: form.timezone,
    endsOn: form.endsMode === 'date' ? form.endsOn : null,
    // the chat is always a destination (deliverToConversation keeps its default)
    recipients: splitRecipients(form.recipients),
    emailRecipients: form.emailMe && mailbox ? [mailbox] : [],
    exportFormat: form.emailMe && mailbox ? form.exportFormat : 'message',
    notifyOnRun: form.notifyOnRun,
    selectedTools: form.selectedTools,
});

export const isFormValid = (form: ScheduleFormState) =>
    Boolean(form.name.trim() && form.prompt.trim()) &&
    (form.frequency === 'custom' || Boolean(form.timeOfDay)) &&
    (form.frequency !== 'monthly' || (form.dayOfMonth >= 1 && form.dayOfMonth <= 31)) &&
    (form.endsMode !== 'date' || Boolean(form.endsOn));
